import React from "react";

// Gesto de "tirar para abajo y soltar" para recargar, como el de las apps nativas.
// Pensado para la pantalla pública de resultados, que se usa casi siempre desde el
// celular: el que está en la cancha tira la pantalla y ve los resultados al día sin
// recargar la página ni perder la fecha que está mirando.
//
// La pantalla scrollea con el documento (no hay contenedor con overflow), así que el
// gesto se escucha en window y solo cuenta si el scroll está arriba de todo.

const UMBRAL = 70; // cuánto hay que bajar para que dispare
const MAXIMO = 110; // hasta dónde estira, por más que se siga tirando
const RESISTENCIA = 0.5; // el dedo recorre el doble de lo que baja la pantalla

export function usePullToRefresh({ onRefresh, activo = true }) {
  const [distancia, setDistancia] = React.useState(0);
  const [refrescando, setRefrescando] = React.useState(false);

  // Los handlers se registran una sola vez: lo que cambia en cada render se lee
  // por ref para no tener que desenganchar y reenganchar los eventos.
  const gesto = React.useRef({ inicioY: 0, tirando: false });
  const distanciaRef = React.useRef(0);
  const refrescandoRef = React.useRef(false);
  const onRefreshRef = React.useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const moverA = React.useCallback((valor) => {
    distanciaRef.current = valor;
    setDistancia(valor);
  }, []);

  React.useEffect(() => {
    if (!activo) return undefined;

    // En iOS el rebote deja el scroll en negativo: eso también es "arriba de todo".
    const enElTope = () => window.scrollY <= 0;

    const alTocar = (e) => {
      if (refrescandoRef.current || e.touches.length !== 1 || !enElTope()) {
        gesto.current.tirando = false;
        return;
      }
      gesto.current = { inicioY: e.touches[0].clientY, tirando: true };
    };

    const alMover = (e) => {
      if (!gesto.current.tirando) return;
      const delta = e.touches[0].clientY - gesto.current.inicioY;
      // Dedo hacia arriba, o ya se scrolleó la página: es un scroll común y el
      // gesto se cancela.
      if (delta <= 0 || !enElTope()) {
        gesto.current.tirando = false;
        if (distanciaRef.current !== 0) moverA(0);
        return;
      }
      // Sin esto el navegador se lleva el gesto como scroll y la pantalla salta.
      if (e.cancelable) e.preventDefault();
      moverA(Math.min(delta * RESISTENCIA, MAXIMO));
    };

    const alSoltar = async () => {
      if (!gesto.current.tirando) return;
      gesto.current.tirando = false;
      if (distanciaRef.current < UMBRAL) {
        moverA(0);
        return;
      }
      refrescandoRef.current = true;
      setRefrescando(true);
      moverA(UMBRAL); // queda enganchado mientras trae los datos
      try {
        await onRefreshRef.current();
      } finally {
        refrescandoRef.current = false;
        setRefrescando(false);
        moverA(0);
      }
    };

    // touchmove NO puede ser pasivo: necesita preventDefault.
    window.addEventListener("touchstart", alTocar, { passive: true });
    window.addEventListener("touchmove", alMover, { passive: false });
    window.addEventListener("touchend", alSoltar);
    window.addEventListener("touchcancel", alSoltar);
    // Chrome en Android trae su propio "tirar para recargar", que recarga la página
    // entera y sacaría al usuario de la fecha que está viendo. Esta clase lo apaga
    // mientras la pantalla está montada.
    document.body.classList.add("tpub-sin-recarga-nativa");

    return () => {
      window.removeEventListener("touchstart", alTocar);
      window.removeEventListener("touchmove", alMover);
      window.removeEventListener("touchend", alSoltar);
      window.removeEventListener("touchcancel", alSoltar);
      document.body.classList.remove("tpub-sin-recarga-nativa");
      gesto.current.tirando = false;
      distanciaRef.current = 0;
    };
  }, [activo, moverA]);

  // Al salir de la pantalla el contenido no puede quedar corrido a medio camino.
  React.useEffect(() => {
    if (!activo && distanciaRef.current !== 0) moverA(0);
  }, [activo, moverA]);

  return { distancia, refrescando };
}

export default usePullToRefresh;
